import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPen, faPlus, faSpinner, faCreditCard, faCheck, faTimes, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import ColumnVisibilityModal from '../../components/ColumnVisibilityModal';
import PagamentoModal from '../../components/PagamentoModal';
import ConclusaoModal from '../../components/ConclusaoModal';
import CancelamentoModal from '../../components/CancelamentoModal';
import CancelamentoPedidoConcluidoModal from '../../components/CancelamentoPedidoConcluidoModal';
import { PedidoPdfButton } from '../../components/PedidoPdfGenerator';
import { Link, useSearchParams } from 'react-router-dom';
import Table from '../../components/Table';
import Pagination from '../../components/Pagination';
import PedidoFilterBar from '../../components/PedidoFilterBar';
import { pedidoService } from '../../services/pedidoService';
import { extensaoService } from '../../services/extensaoService';
import { useAuth } from '../../contexts/AuthContext';

const columns = [
  { Header: 'ID', accessor: 'idProduto' },
  { Header: 'Status', accessor: 'status' },
  { Header: 'Quem pediu', accessor: 'pessoaNome' },
  { Header: 'Valor', accessor: 'valor' },
  { Header: 'Pago', accessor: 'pagoTexto' },
  { Header: 'Forma Entrega', accessor: 'formaEntregaNome' },
  { Header: 'Forma Pagamento', accessor: 'formaPagamentoNome' },
  { Header: 'Prazo Entrega', accessor: 'prazoEntregaNome' },
  { Header: 'Atribuído a', accessor: 'user' },
  { Header: 'Cadastrado Por', accessor: 'cadastradoPor' },
  { Header: 'Cadastrado Em', accessor: 'cadastradoEm', Cell: (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('pt-BR');
  }},
  { Header: 'Pago Em', accessor: 'pagoEm', Cell: (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('pt-BR');
  }},
  { Header: 'Concluído Em', accessor: 'concluidoEm', Cell: (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('pt-BR');
  }},
  { Header: 'Ações', accessor: 'acoes' }
];

const pageSizes = [5, 10, 20, 50];

const PedidosList = () => {
  const { hasPermission, user } = useAuth();
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingFilter, setLoadingFilter] = useState(false); // Loading específico para filtros
  const [error, setError] = useState('');
  const [searching, setSearching] = useState(false); // Novo estado para indicar busca em andamento
  
  // Permite chegar nesta tela com filtros pré-aplicados via querystring.
  // Exemplos:
  //   /pedidos?status=Pendente&dataInicio=2026-04-26&dataFim=2026-05-26
  //   /pedidos?naoPago=true                   (drill-down do KPI Inadimplência)
  // Filtros aceitos hoje: status, idProduto, cadastradoPor, user, extensao,
  // dataInicio, dataFim (YYYY-MM-DD), naoPago ('true'/'1' → pagoEm IS NULL).
  const [searchParams] = useSearchParams();
  const initialFilters = {
    idProduto: searchParams.get('idProduto') || '',
    status: searchParams.get('status') || '',
    cadastradoPor: searchParams.get('cadastradoPor') || '',
    user: searchParams.get('user') || '',
    extensao: searchParams.get('extensao') || '',
    extensaoSearch: '',
    dataInicio: searchParams.get('dataInicio') || '',
    dataFim: searchParams.get('dataFim') || '',
    naoPago: searchParams.get('naoPago') || '',
    pago: searchParams.get('pago') || '',
    vencido: searchParams.get('vencido') || '',
    inadimplente: searchParams.get('inadimplente') || '',
    pagoNaoConcluido: searchParams.get('pagoNaoConcluido') || ''
  };

  // Estados separados: um para input (sem delay) e outro para filtro (com delay)
  const [inputValues, setInputValues] = useState(initialFilters);
  const [filters, setFilters] = useState(initialFilters);
  
  // Estados para extensões
  const [extensoes, setExtensoes] = useState([]);
  const [extensoesFiltradas, setExtensoesFiltradas] = useState([]);
  
  // Estados para modais administrativos
  const [pagamentoModal, setPagamentoModal] = useState({ isOpen: false, pedido: null });
  const [conclusaoModal, setConclusaoModal] = useState({ isOpen: false, pedido: null });
  const [cancelamentoModal, setCancelamentoModal] = useState({ isOpen: false, pedido: null });
  const [cancelamentoConcluidoModal, setCancelamentoConcluidoModal] = useState({ isOpen: false, pedido: null });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizes[1]); // Default: 10
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    page: 1,
    limit: 10
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columns.map(col => col.accessor));

  // Sincronizar inputValues com filters após debounce (apenas para campos de texto)
  useEffect(() => {
    setSearching(true); // Indica que está aguardando para buscar
    const delayTimer = setTimeout(() => {
      setFilters(inputValues);
      setPage(1); // Reset página após filtro
      setSearching(false); // Busca iniciada
    }, 800); // Aumentado para 800ms para melhor experiência de digitação

    return () => {
      clearTimeout(delayTimer);
      setSearching(false);
    };
  }, [inputValues]);

  // Carregamento dos dados quando filters ou página mudam
  useEffect(() => {
    // Se filters mudou mas não é o primeiro render, é uma mudança de filtro
    const isFilterChange = filters.idProduto !== '' || filters.status !== '' || filters.cadastradoPor !== '' || filters.user !== '' || filters.extensao !== '';
    loadData(isFilterChange);
  }, [page, pageSize, filters]);

  // Carregar extensões ao montar o componente
  useEffect(() => {
    const loadExtensoes = async () => {
      try {
        const result = await extensaoService.list({ page: 1, limit: 1000 });
        if (result.success) {
          setExtensoes(result.data || []);
          setExtensoesFiltradas(result.data || []);
        }
      } catch (error) {
        console.error('Erro ao carregar extensões:', error);
      }
    };
    loadExtensoes();
  }, []);

  // Verificar permissão de acesso
  if (!hasPermission('pedidos.list')) {
    return (
      <div className="flex-1 w-full max-w-full overflow-x-hidden">
        <div className="px-2 sm:px-6">
          <div className="flex justify-center items-center py-8">
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
              Você não tem permissão para acessar esta funcionalidade.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const loadData = async (isFilterChange = false) => {
    // Se é mudança de filtro, usa loadingFilter ao invés de loading
    if (isFilterChange) {
      setLoadingFilter(true);
    } else {
      setLoading(true);
    }
    setError('');
    try {
      const result = await pedidoService.getAll(filters, page, pageSize);
      if (result.success) {
        setPedidos(result.data);
        
        setPagination(result.pagination || {
          total: 0,
          totalPages: 0,
          page: 1,
          limit: pageSize
        });
      } else {
        setError(result.error || 'Erro ao carregar pedidos');
      }
    } catch (err) {
      setError('Erro ao carregar pedidos');
      console.error('Erro:', err);
    } finally {
      setLoading(false);
      setLoadingFilter(false);
    }
  };

  // Modal handlers
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const handleToggleColumn = (accessor) => {
    setVisibleColumns(prev =>
      prev.includes(accessor)
        ? prev.filter(col => col !== accessor)
        : [...prev, accessor]
    );
  };

  const handleFilterChange = (e) => {
    // Atualiza inputValues imediatamente (mantém foco no campo)
    setInputValues({ ...inputValues, [e.target.name]: e.target.value });
    // filters será atualizado após 500ms pelo useEffect com debounce
  };

  const handleExtensaoSearch = (e) => {
    const searchValue = e.target.value;
    setInputValues({ ...inputValues, extensaoSearch: searchValue });
    
    // Filtrar extensões em tempo real
    if (searchValue) {
      const filtered = extensoes.filter(ext => 
        ext.nome.toLowerCase().includes(searchValue.toLowerCase())
      );
      setExtensoesFiltradas(filtered);
      
      // Se encontrou extensão exata, atualiza o filtro
      const extensaoExata = extensoes.find(ext => 
        ext.nome.toLowerCase() === searchValue.toLowerCase()
      );
      if (extensaoExata) {
        setInputValues(prev => ({ ...prev, extensao: extensaoExata.idExtensao }));
      } else {
        setInputValues(prev => ({ ...prev, extensao: '' }));
      }
    } else {
      setExtensoesFiltradas(extensoes);
      setInputValues(prev => ({ ...prev, extensao: '' }));
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Handlers para modais administrativos
  const handleOpenPagamento = (pedido) => {
    setPagamentoModal({ isOpen: true, pedido });
  };

  const handleClosePagamento = () => {
    setPagamentoModal({ isOpen: false, pedido: null });
  };

  const handlePaymentSuccess = (updatedPedido) => {
    setPedidos(prev => prev.map(p => 
      p.idProduto === updatedPedido.idProduto ? updatedPedido : p
    ));
    // Success message handled in modal
  };

  const handleOpenConclusao = (pedido) => {
    setConclusaoModal({ isOpen: true, pedido });
  };

  const handleCloseConclusao = () => {
    setConclusaoModal({ isOpen: false, pedido: null });
  };

  const handleConclusionSuccess = (updatedPedido) => {
    setPedidos(prev => prev.map(p => 
      p.idProduto === updatedPedido.idProduto ? updatedPedido : p
    ));
    // Success message handled in modal
  };

  const handleOpenCancelamento = (pedido) => {
    setCancelamentoModal({ isOpen: true, pedido });
  };

  const handleCloseCancelamento = () => {
    setCancelamentoModal({ isOpen: false, pedido: null });
  };

  const handleCancelSuccess = (updatedPedido) => {
    setPedidos(prev => prev.map(p => 
      p.idProduto === updatedPedido.idProduto ? updatedPedido : p
    ));
    // Success message handled in modal
  };

  // Handlers para cancelamento de pedidos concluídos
  const handleOpenCancelamentoConcluido = (pedido) => {
    setCancelamentoConcluidoModal({ isOpen: true, pedido });
  };

  const handleCloseCancelamentoConcluido = () => {
    setCancelamentoConcluidoModal({ isOpen: false, pedido: null });
  };

  const handleCancelConcluidoSuccess = (result) => {
    // Recarregar a lista para refletir as mudanças
    loadData();
    
    // Mostrar mensagem de sucesso
    const message = result.data?.message || 'Operação realizada com sucesso';
    // Você pode adicionar um toast/notification aqui se tiver um sistema de notificações
    console.log('Sucesso:', message);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1); // Reset to first page when changing page size
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Pendente':
        return 'bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium';
      case 'Concluido':
        return 'bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium';
      case 'Cancelado':
        return 'bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium';
      case 'Rascunho':
        return 'bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium';
      default:
        return 'bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium';
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex-1 w-full max-w-full overflow-x-hidden">
        <div className="px-2 sm:px-6">
          <div className="flex justify-center items-center py-8">
            <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-green-600" />
            <span className="ml-2 text-gray-600">Carregando pedidos...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 w-full max-w-full overflow-x-hidden">
      <div className="px-2 sm:px-6">
        <div className="max-w-full">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Listagem de Pedidos</h2>
            {hasPermission('pedidos.create') && (
              <Link
                to="/pedidos/create"
                className="px-4 py-2 rounded bg-green-600 text-white font-semibold shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faPlus} />
                <span>Novo Pedido</span>
              </Link>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <ColumnVisibilityModal
            columns={columns}
            visibleColumns={visibleColumns}
            onChange={handleToggleColumn}
            onClose={handleCloseModal}
            isOpen={isModalOpen}
          />

          <div className="max-w-full">
            <PedidoFilterBar 
              filters={inputValues} 
              onChange={handleFilterChange}
              onExtensaoSearch={handleExtensaoSearch}
              extensoes={extensoesFiltradas}
              searching={searching}
            />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2 max-w-full">
            <div className="order-2 sm:order-1 w-full sm:w-auto">
              <Pagination 
                currentPage={pagination.page} 
                totalPages={pagination.totalPages}
                totalItems={pagination.total}
                itemsPerPage={pageSize}
                onPageChange={handlePageChange} 
              />
            </div>
            <div className="order-1 sm:order-2 flex items-center w-full sm:w-auto justify-end gap-2">
              {/* O total já aparece no <Pagination> ("Mostrando X a Y de Z");
                  repetir aqui só duplicava a informação e estourava no mobile */}
              <label htmlFor="pageSize" className="text-sm text-gray-700 whitespace-nowrap mr-2">Por página:</label>
              <select
                id="pageSize"
                value={pageSize}
                onChange={e => handlePageSizeChange(Number(e.target.value))}
                className="px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
              >
                {pageSizes.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <button
                type="button"
                className="flex items-center gap-2 px-3 py-1 rounded bg-green-600 text-white font-medium shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400"
                onClick={handleOpenModal}
                aria-label="Exibir/Ocultar colunas"
              >
                <FontAwesomeIcon icon={faEye} />
                <span>Colunas</span>
              </button>
            </div>
          </div>

          <div className="max-w-full">
            <Table
              columns={columns.filter(col => visibleColumns.includes(col.accessor)).map(col => {
                if (col.accessor === 'idProduto') {
                  return {
                    ...col,
                    Cell: (value, row) => {
                      // Verificar se pode editar: 
                      // - Precisa ter permissão pedidos.update
                      // - Apenas Rascunho pode ser editado por qualquer usuário
                      // - Pendente, Concluído e Cancelado: apenas Admin (nivel_acesso >= 4)
                      const statusRestrito = ['Pendente', 'Concluido', 'Cancelado'].includes(row.status);
                      const canEdit = hasPermission('pedidos.update') && 
                        (!statusRestrito || (user?.nivel_acesso >= 4));
                      
                      return (
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{value}</span>
                          {canEdit && (
                            <Link
                              to={`/pedidos/${row.idProduto}/edit`}
                              className="text-green-600 hover:text-green-800 p-1 rounded focus:outline-none"
                              title="Editar"
                            >
                              <FontAwesomeIcon icon={faPen} />
                            </Link>
                          )}
                        </div>
                      );
                    }
                  };
                } else if (col.accessor === 'status') {
                  return {
                    ...col,
                    Cell: (value) => (
                      <span className={getStatusBadgeClass(value)}>
                        {value}
                      </span>
                    )
                  };
                } else if (col.accessor === 'valor') {
                  return {
                    ...col,
                    Cell: (value) => (
                      <span className="font-medium text-green-600">
                        {formatCurrency(value)}
                      </span>
                    )
                  };
                } else if (col.accessor === 'pagoTexto') {
                  return {
                    ...col,
                    Cell: (value) => (
                      <span className={value === 'Sim' ? 'text-green-600 font-semibold' : 'text-red-600'}>
                        {value}
                      </span>
                    )
                  };
                } else if (col.accessor === 'acoes') {
                  return {
                    ...col,
                    Cell: (value, row) => (
                      <div className="flex items-center gap-1">
                        {/* Botão PDF - disponível se tiver permissão de download */}
                        {hasPermission('pedidos.download') && (
                          <PedidoPdfButton 
                            pedidoId={row.idProduto} 
                            size="sm" 
                            variant="outline"
                          />
                        )}
                        
                        {/* Botão de receber pagamento - apenas para pedidos não pagos e não em rascunho */}
                        {hasPermission('pedidos.receive_payment') && row.pagoTexto !== 'Sim' && row.status !== 'Cancelado' && row.status !== 'Concluido' && row.status !== 'Rascunho' && (
                          <button
                            onClick={() => handleOpenPagamento(row)}
                            className="text-blue-600 hover:text-blue-800 p-1 rounded focus:outline-none"
                            title="Receber Pagamento"
                          >
                            <FontAwesomeIcon icon={faCreditCard} />
                          </button>
                        )}
                        
                        {/* Botão de concluir - apenas para pedidos pagos, pendentes e não em rascunho */}
                        {hasPermission('pedidos.conclude') && row.pagoTexto === 'Sim' && row.status === 'Pendente' && row.status !== 'Rascunho' && (
                          <button
                            onClick={() => handleOpenConclusao(row)}
                            className="text-green-600 hover:text-green-800 p-1 rounded focus:outline-none"
                            title="Concluir Pedido"
                          >
                            <FontAwesomeIcon icon={faCheck} />
                          </button>
                        )}
                        
                        {/* Botão de cancelar - para pedidos não concluídos */}
                        {hasPermission('pedidos.cancel') && row.status !== 'Cancelado' && row.status !== 'Concluido' && (
                          <button
                            onClick={() => handleOpenCancelamento(row)}
                            className="text-red-600 hover:text-red-800 p-1 rounded focus:outline-none"
                            title="Cancelar Pedido"
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        )}

                        {/* Botão de cancelar pedido concluído - somente para usuários com permissão */}
                        {hasPermission('pedidos.cancel') && row.status === 'Concluido' && (
                          <button
                            onClick={() => handleOpenCancelamentoConcluido(row)}
                            className="text-orange-600 hover:text-orange-800 p-1 rounded focus:outline-none"
                            title="Cancelar Pedido Concluído"
                          >
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                          </button>
                        )}
                      </div>
                    )
                  };
                }
                return col;
              })}
              data={pedidos}
            />
          </div>

          {pedidos.length === 0 && !loading && (
            <div className="text-center py-8 text-gray-500">
              Nenhum pedido encontrado.
            </div>
          )}
        </div>
        
        {/* Modais Administrativos */}
        <PagamentoModal
          isOpen={pagamentoModal.isOpen}
          onClose={handleClosePagamento}
          pedido={pagamentoModal.pedido}
          onPaymentSuccess={handlePaymentSuccess}
        />
        
        <ConclusaoModal
          isOpen={conclusaoModal.isOpen}
          onClose={handleCloseConclusao}
          pedido={conclusaoModal.pedido}
          onConclusionSuccess={handleConclusionSuccess}
        />
        
        <CancelamentoModal
          isOpen={cancelamentoModal.isOpen}
          onClose={handleCloseCancelamento}
          pedido={cancelamentoModal.pedido}
          onCancelSuccess={handleCancelSuccess}
        />
        
        <CancelamentoPedidoConcluidoModal
          isOpen={cancelamentoConcluidoModal.isOpen}
          onClose={handleCloseCancelamentoConcluido}
          pedido={cancelamentoConcluidoModal.pedido}
          onCancelSuccess={handleCancelConcluidoSuccess}
        />
      </div>
    </div>
  );
};

export default PedidosList;
